import { createReducer } from "redux-create-reducer";
import { AUTH_ACTIONS } from "./authActions";
import { MISC_AUTH_ACTIONS, RESET_MESSAGES } from "../misc/miscActions";
import { REHYDRATE } from "redux-persist/constants";
import isAxiosError from "assets/scripts/utils/isAxiosError";
import { LOGIN_URL, LOGOUT_URL } from "api/accounts";
import moment from "moment";
import { VALIDATION_ACTIONS } from "../validation/validationActions";
import { LOCATION_CHANGE } from "react-router-redux";
const VERSION = 5;
const INITIAL_STATE = {
    version: VERSION,
    isLoading: false,
    loggedIn: false,
    isRequiredCaptcha: false,
    isSuccess: null,
    isPasswordResetSuccess: null,
    fieldErrors: [],
    isSuccessContinueSession: null,
    isSuccessLogout: null,
    sessionLength: null,
    sessionRefreshTimestamp: moment().toISOString(),
    sessionWarnTimestamp: null,
    lastLogin: null,
    isForgotPasswordRateLimited: false
};
/**
 * Reducer creation and export.
 * Uses the constants above to define new state for each action
 */
export default createReducer(INITIAL_STATE, {
    /* --- Account creation --- */
    [AUTH_ACTIONS.CREATE_STARTED](state, _action) {
        return {
            ...state,
            isLoading: true,
            loggedIn: false,
            error: null,
            fieldErrors: [],
            isSuccessLogout: null,
            isForgotPasswordRateLimited: false
        };
    },
    [AUTH_ACTIONS.CREATE_FAILED](state, action) {
        return {
            ...state,
            isLoading: false,
            error: action.payload,
            isSuccessLogout: null
        };
    },
    [AUTH_ACTIONS.CREATE_SUCCEEDED_FALSE](state, action) {
        return {
            ...state,
            isLoading: false,
            isSuccess: false,
            fieldErrors: action.payload,
            loggedIn: false,
            isSuccessLogout: null
        };
    },
    [AUTH_ACTIONS.CREATE_SUCCEEDED_TRUE](state, action) {
        return {
            ...state,
            isLoading: false,
            isSuccess: false,
            userId: action.payload,
            loggedIn: true,
            isSuccessLogout: null
        };
    },
    [VALIDATION_ACTIONS.REGISTRATION_COMPLETE](state, action) {
        return {
            ...state,
            isLoading: false,
            isSuccess: false,
            userId: action.payload.account.userId,
            loggedIn: true,
            isSuccessLogout: null
        };
    },
    /* --- login/logut --- */
    [AUTH_ACTIONS.LOGOUT](state, action) {
        return {
            ...state,
            isLoading: false,
            loggedIn: false,
            isRequiredCaptcha: false,
            isSuccessLogout: action.payload,
            sessionRefreshTimestamp: null,
            sessionWarnTimestamp: null,
            lastLogin: null
        };
    },
    [AUTH_ACTIONS.LOGIN_STARTED](state, _action) {
        return {
            ...state,
            isLoading: true,
            error: null,
            fieldErrors: [],
            isSuccessLogout: null,
            isForgotPasswordRateLimited: false
        };
    },
    [AUTH_ACTIONS.LOGIN_FAILED](state, action) {
        let isRequiredCaptcha = state.isRequiredCaptcha;
        if (isAxiosError(action.payload)
            && (action.payload.response.status === 417 || action.payload.response.status === 429)) {
            isRequiredCaptcha = true;
        }
        return {
            ...state,
            isLoading: false,
            error: action.payload,
            isRequiredCaptcha,
            isSuccessLogout: null
        };
    },
    [AUTH_ACTIONS.LOGIN_SUCCEEDED_FALSE](state, action) {
        return {
            ...state,
            isLoading: false,
            isSuccess: false,
            fieldErrors: action.payload,
            loggedIn: false,
            isSuccessLogout: null
        };
    },
    [AUTH_ACTIONS.LOGIN_SUCCEEDED_TRUE](state, action) {
        return {
            ...state,
            isLoading: false,
            isSuccess: null,
            userId: action.payload.userId,
            lastLogin: action.payload.lastLogin,
            loggedIn: true,
            isSuccessLogout: null
        };
    },
    /* --- security questions --- */
    [AUTH_ACTIONS.QUESTION_FETCH_STARTED](state, _action) {
        return {
            ...state,
            isLoading: true,
            error: null,
            fieldErrors: [],
            isSuccessLogout: null
        };
    },
    [AUTH_ACTIONS.QUESTION_FETCH_FAILED](state, action) {
        return {
            ...state,
            isLoading: false,
            error: action.payload,
            isSuccessLogout: null
        };
    },
    [AUTH_ACTIONS.QUESTION_FETCH_SUCCEEDED](state, action) {
        return {
            ...state,
            isLoading: false,
            questions: action.payload,
            isSuccessLogout: null
        };
    },
    /* --- current security question --- */
    [AUTH_ACTIONS.CURRENT_QUESTION_FETCH_STARTED](state, _action) {
        return {
            ...state,
            isLoading: true,
            error: null,
            fieldErrors: [],
            isSuccessLogout: null
        };
    },
    [AUTH_ACTIONS.CURRENT_QUESTION_FETCH_FAILED](state, action) {
        return {
            ...state,
            isLoading: false,
            error: action.payload,
            isSuccessLogout: null
        };
    },
    [AUTH_ACTIONS.CURRENT_QUESTION_FETCH_SUCCEEDED](state, action) {
        return {
            ...state,
            isLoading: false,
            currentQuestion: action.payload,
            isSuccessLogout: null
        };
    },
    /* --- Change Password --- */
    [AUTH_ACTIONS.CHANGE_STARTED](state, _action) {
        return {
            ...state,
            isLoading: true,
            isSuccess: null,
            error: null,
            fieldErrors: [],
            isSuccessLogout: null
        };
    },
    [AUTH_ACTIONS.CHANGE_FAILED](state, action) {
        return {
            ...state,
            isLoading: false,
            error: action.payload,
            isSuccessLogout: null
        };
    },
    [AUTH_ACTIONS.CHANGE_SUCCEEDED_FALSE](state, action) {
        return {
            ...state,
            isLoading: false,
            fieldErrors: action.payload,
            isSuccess: false,
            isSuccessLogout: null
        };
    },
    [AUTH_ACTIONS.CHANGE_SUCCEEDED_TRUE](state, _action) {
        return {
            ...state,
            isLoading: false,
            isSuccess: true,
            isSuccessLogout: null
        };
    },
    /* --- Forgot Password (to send email with link) --- */
    [AUTH_ACTIONS.FORGOT_STARTED](state, _action) {
        return {
            ...state,
            isLoading: true,
            error: null,
            fieldErrors: [],
            isSuccessLogout: null,
            isForgotPasswordRateLimited: false
        };
    },
    [AUTH_ACTIONS.FORGOT_FAILED](state, action) {
        if (isAxiosError(action.payload) && action.payload.response.status === 429) {
            return {
                ...state,
                isLoading: false,
                isSuccessLogout: null,
                isForgotPasswordRateLimited: true
            };
        }
        return {
            ...state,
            isLoading: false,
            error: action.payload,
            isSuccessLogout: null,
            isForgotPasswordRateLimited: false
        };
    },
    [AUTH_ACTIONS.FORGOT_SUCCEEDED_FALSE](state, action) {
        return {
            ...state,
            isLoading: false,
            isSuccess: false,
            fieldErrors: action.payload,
            isSuccessLogout: null
        };
    },
    [AUTH_ACTIONS.FORGOT_SUCCEEDED_TRUE](state, _action) {
        return {
            ...state,
            isLoading: false,
            isSuccess: true,
            isSuccessLogout: null
        };
    },
    /* --- Reset Password (after link in email has been clicked) --- */
    [AUTH_ACTIONS.RESET_STARTED](state, _action) {
        return {
            ...state,
            isLoading: true,
            error: null,
            fieldErrors: [],
            isSuccessLogout: null,
            isPasswordResetSuccess: null
        };
    },
    [AUTH_ACTIONS.RESET_FAILED](state, action) {
        let isRequiredCaptcha = state.isRequiredCaptcha;
        if (action.payload.response.status === 417 || action.payload.response.status === 429) {
            isRequiredCaptcha = true;
        }
        return {
            ...state,
            error: action.payload,
            isLoading: false,
            loggedIn: false,
            isRequiredCaptcha,
            isSuccessLogout: null,
            isPasswordResetSuccess: false
        };
    },
    [AUTH_ACTIONS.RESET_SUCCEEDED_FALSE](state, action) {
        return {
            ...state,
            isLoading: false,
            loggedIn: false,
            isPasswordResetSuccess: false,
            fieldErrors: action.payload,
            isSuccessLogout: null
        };
    },
    [AUTH_ACTIONS.RESET_SUCCEEDED_TRUE](state, _action) {
        return {
            ...state,
            isLoading: false,
            isPasswordResetSuccess: true,
            isSuccessLogout: null
        };
    },
    /* --- session timeout --- */
    [AUTH_ACTIONS.SESSION_TIMEOUT_STATUS](state, _action) {
        return {
            ...state,
            sessionWarnTimestamp: null,
            isSuccessContinueSession: true
        };
    },
    [AUTH_ACTIONS.RETRIEVE_SESSION_LENGTH_SUCCEEDED](state, action) {
        return {
            ...state,
            sessionLength: action.payload
        };
    },
    [AUTH_ACTIONS.RETRIEVE_SESSION_LENGTH_FAILED](state, _action) {
        return {
            ...state,
            isLoading: false
        };
    },
    [AUTH_ACTIONS.NEW_SESSION_FAILED](state, _action) {
        return {
            ...state,
            isLoading: false
        };
    },
    /* --- Reset Status Messages --- */
    [MISC_AUTH_ACTIONS.RESET_MESSAGES](state, _action) {
        return {
            ...state,
            error: null,
            isSuccess: null,
            isLoading: false,
            isSuccessLogout: null,
            isSuccessContinueSession: null,
            fieldErrors: []
        };
    },
    [RESET_MESSAGES](state, { payload }) {
        switch (payload) {
            case "auth-password-reset":
                return {
                    ...state,
                    isPasswordResetSuccess: null
                };
        }
        return state;
    },
    /* --- Captcha --- */
    [MISC_AUTH_ACTIONS.TOGGLE_CAPTCHA](state, action) {
        let isRequiredCaptcha = state.isRequiredCaptcha;
        if (action.payload.response.status === 417 || action.payload.response.status === 429) {
            isRequiredCaptcha = true;
        }
        return {
            ...state,
            isRequiredCaptcha
        };
    },
    [AUTH_ACTIONS.API_REQUEST](state, { payload }) {
        if (payload.statusId != null
            && payload.statusId > 0
            && payload.statusId !== 401
            && payload.url !== LOGOUT_URL
            && (payload.authenticated
                || payload.url === LOGIN_URL)
            && (state.sessionRefreshTimestamp == null ||
                moment(state.sessionRefreshTimestamp).add(30 * (state.sessionLength || 0), "seconds") < moment(payload.timestamp))) {
            return {
                ...state,
                sessionRefreshTimestamp: payload.timestamp
            };
        }
        return state;
    },
    [AUTH_ACTIONS.SESSION_WARNING](state, { payload }) {
        return {
            ...state,
            sessionWarnTimestamp: payload.timestamp
        };
    },
    [LOCATION_CHANGE](state) {
        return {
            ...state,
            isForgotPasswordRateLimited: false
        };
    },
    [REHYDRATE](state, action) {
        const { auth } = action.payload;
        if (auth && auth.version === VERSION) {
            return {
                ...auth,
                // exclude these fields from persisting
                error: state.error,
                isSuccessLogout: state.isSuccessLogout,
                isLoading: state.isLoading,
                version: state.version,
                currentQuestion: state.currentQuestion,
                isPasswordResetSuccess: state.isPasswordResetSuccess,
                isSuccessContinueSession: state.isSuccessContinueSession,
                sessionRefreshTimestamp: state.sessionRefreshTimestamp,
                sessionWarnTimestamp: state.sessionWarnTimestamp,
                isForgotPasswordRateLimited: state.isForgotPasswordRateLimited
            };
        }
        return { ...state };
    }
});



// WEBPACK FOOTER //
// ./src/assets/scripts/domain/auth/authReducer.ts